import * as angular from 'angular';
import { find, findIndex } from 'lodash';

import { DatasourceResourceHelper, DeprecatedApiService } from '@mode/legacy/data-access-model-js';
import { QueryOptions, RecordTypes } from '@mode/shared/data-access-algolia';
import { SelectOption } from '@mode/shared/ui-angularjs';
import { BugsnagHelpers } from '~/lib/bugsnag-helpers';
import { EntitledActionState } from '~/modules/org-settings/components/connection/connection-permissions/connection-permissions-access-dropdown/connection-permissions-access-dropdown.component';

import template from './connection-permissions-add-entitlements-modal.html';
import { SearchClientService } from '@mode/algolia/domain';

//
// Types for algolia
export type PermCandidateOptions = QueryOptions<'userEmail' | 'group'> & {
  rawResults?: true;
};

class ConnectionPermissionsAddEntitlementsModalController {
  public datasourceToken: string;
  public orgToken: string;
  public orgName: string;
  public queryString: string;
  public $select: string;
  private selectedCandidateList = [];
  private permissionCandidatesList: any[];
  private accessOptions: EntitledActionState[] = [];
  private datasource: any;
  public allPermissionCallsHaveSucceeded: boolean | null = null; // true if calls 200, false if calls fail, null if not yet sent
  public showSpinner = false;

  static $inject = ['$scope', '$state', '$timeout', 'SearchClientService', 'DataSourceModel', 'ApiService'];

  constructor(
    private $scope,
    private $state,
    private $timeout,
    private _searchClientService: SearchClientService,
    private DataSourceModel,
    private ApiService: DeprecatedApiService
  ) {}

  $onInit() {
    this.accessOptions = this.getAccessOptions();

    this.updateQuery();
  }

  /**
   * @todo (Franklin) 04/29/2020: Move to service and add coverage {@link https://github.com/mode/issues/issues/3506}
   */
  queryPermissionCandidates(query = '') {
    const opts: PermCandidateOptions = {
      query: query || '',
      indexes: ['userEmail', 'group'],
      perPage: 10,
      rawResults: true,
      filters: {
        userEmail: `organization_${this.orgToken},organization_${this.orgToken}_active`,
        group: `organization_${this.orgToken}`,
      },
      facets: {
        // use negation syntax (https://www.algolia.com/doc/api-reference/api-parameters/facetFilters/#usage-notes)
        // to exclude users that have individual entitlements but display those that have
        // access otherwise so we can still give them access as an individual
        userEmail: `connection_permission:-${this.datasourceToken}_individual`,
        // exclude groups if they are already entitled to the data source
        // exclude the everyone group
        group: `connection_permission:-${this.datasourceToken},groupType:-everyone`,
      },
    };

    const queryPromise = this._searchClientService.query(opts);

    return queryPromise
      .then((response) => this.handlePerimssionsSearchResponse(response))
      .catch((err) => BugsnagHelpers.notifySafely(err));
  }

  handlePerimssionsSearchResponse(algoliaResults) {
    let hitsList = [];
    algoliaResults.results.forEach((result) => {
      hitsList = hitsList.concat(result.hits);
    });
    this.permissionCandidatesList = hitsList;
  }

  updateQuery(query = '') {
    this.queryPermissionCandidates(query);
  }

  handlePermissionLevelChange(option: SelectOption, targetCandidate: RecordTypes['userEmail'] | RecordTypes['group']) {
    this.updateCandidateEntitlement(targetCandidate.token, targetCandidate.objectType, option.value.toString());
  }

  // Dynamically set enabled options from the permissions resource
  getAccessOptions(): EntitledActionState[] {
    try {
      return DatasourceResourceHelper.getPermissionOptions(this.datasource._resource) as EntitledActionState[];
    } catch (e) {
      BugsnagHelpers.notifySafely(e);
      console.error(e);
    }

    return [];
  }

  updateCandidateEntitlement(candidateToken: string, candidateType: string, newEntitlmentType: string) {
    // find the index of the candidate who is updating their permission type
    const targetIndex = findIndex(this.selectedCandidateList, {
      objectType: candidateType,
      token: candidateToken,
    });

    // update that target candidate to reflect the new value
    this.selectedCandidateList[targetIndex].entitlementType = newEntitlmentType;
  }

  handleCandidateRemove(data: RecordTypes['userEmail'] | RecordTypes['group']) {
    const filteredList = this.selectedCandidateList.filter(
      (candidate) => !(candidate.token === data.token && candidate.objectType === data.objectType)
    );

    this.selectedCandidateList = filteredList;
  }

  clearSeachInputFocus() {
    setTimeout(function () {
      $(':focus').blur();
    });
  }

  handleCandidateAdd(data, $select) {
    if (
      // if the incoming candidate is not already part of the selected candidates list
      !find(this.selectedCandidateList, {
        objectType: data.objectType,
        token: data.token,
      })
    ) {
      // add a default value for the dropdown
      data.entitlementType = 'query';

      // then add the candidate to the list
      this.selectedCandidateList.push(data);
    }

    // finally reset the filter
    this.queryString = '';

    // Remove the focus from the UI select
    this.clearSeachInputFocus();
  }

  isCandidateAddedToList(data) {
    return find(this.selectedCandidateList, {
      objectType: data.objectType,
      token: data.token,
    });
  }

  handleSearch(event, data) {
    // 40, 38, 13 are they "up", "down", and "enter" keys respectfully
    const navigationKeyCodes = [40, 38, 13];
    // only re-query algolia if they keystroke are not
    // from the user navigating with the arrow keys || enter
    if (!navigationKeyCodes.includes(event.keyCode)) {
      this.updateQuery(data);
    }
  }

  addGrantPermissionToDataSource(candidate) {
    return this.ApiService.create(this.datasource._resource, 'permissions', {
      'permission.resource_type': 'data_source',
      'permission.resource_token': this.datasource.token,
      'permission.action': candidate.entitlementType,
      'permission.accessor_type': candidate.objectType === 'user_group' ? 'UserGroup' : 'Account',
      'permission.accessor_token': candidate.token,
    }).catch((err) => {
      console.error('Error creating entitlement', err);
      return Promise.reject(err);
    });
  }

  submitRequests() {
    this.showSpinner = true;
    const grantRequests = this.selectedCandidateList.map((candidate) => this.addGrantPermissionToDataSource(candidate));
    Promise.all(grantRequests)
      .then((res) => {
        this.$scope.$evalAsync(() => {
          // intentional delay to allow BE to sync Algolia index
          this.$timeout(() => {
            this.showSpinner = false;
            this.allPermissionCallsHaveSucceeded = true;
            this.$state.reload(true);
          }, 4000);
        });
      })
      .catch((err) => {
        this.$scope.$evalAsync(() => {
          this.showSpinner = false;
          this.allPermissionCallsHaveSucceeded = false;
        });
        BugsnagHelpers.notifySafely(err);
        console.error(err);
      });
  }
}

export const ConnectionPermissionsAddEntitlementsModalDirective = function (Modal) {
  return {
    restrict: 'A',
    scope: {
      datasource: '<',
    },
    link: function ($scope: angular.IScope, elem, attrs) {
      elem.on('click.context', () => {
        Modal.open(
          {
            orgToken: attrs.orgToken,
            orgName: attrs.orgName,
            datasourceToken: attrs.datasourceToken,
            datasource: $scope.datasource,
          },
          {
            controller: ConnectionPermissionsAddEntitlementsModalController,
            controllerAs: 'ctrl',
            bindToController: true,
            template: template,
          }
        );
      });

      // cleanup handlers
      $scope.$on('$destroy', () => elem.off('click.context'));
    },
  };
};

ConnectionPermissionsAddEntitlementsModalDirective.$inject = ['ModalService'];
